import { useEffect, useState, useCallback, Suspense, lazy, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Box, CircularProgress, Tabs, Tab, useTheme, useMediaQuery, Dialog } from '@mui/material'
import { styled as muiStyled } from '@mui/material'
import SupportHeader from 'src/supportPortal/layout/SupportHeader'
import SupportContactActions from 'src/supportPortal/components/contact/SupportContactActions'
import SupportBookedEventRescheduleDrawer from 'src/supportPortal/components/bookings/SupportBookedEventRescheduleDrawer'
import { getUserRoles } from 'src/mentor/layout/utils'
import CoreBookingsListing from 'src/mentor/components/MentorEventsPage/components/bookingListing/CoreBookingsListing'
import { getBookedSessionsForMentorSupport } from 'src/supportPortal/services/bookingsService'
import { getSPDataWithoutParams } from 'src/supportPortal/services/services'
import { getCPDataWithParams } from 'src/clinic/services'
import {
  getDataMapFromCommonResponse,
  isCommonResponseSuccessfulV2,
} from 'src/mentor/helpers/utilityFunctions'
import { SESSION_PLATFORM_ONLINE, SESSION_PLATFORM_OFFLINE, BOOKING_STATUSES, BOOKING_PAYMENT_STATUSES } from 'src/consumer/constants'
import { ReceiptPrintDialogPaper } from 'src/clinic/components/ClinicManageBookingsPage/receiptPrinting/styledComponents'
import { getAllowedSupportBookedEventTypeOptions } from 'src/supportPortal/access/accessMatrix'

// Lazy load components
const BookedEventDetailsDrawer = lazy(() =>
  import('src/clinic/components/ClinicManageBookingsPage/components/BookedEventDetailsDrawer')
)
const JoiningDetails = lazy(() =>
  import('src/clinic/components/ClinicManageBookingsPage/slotItem/component/JoiningDetails')
)
const BookedEventLogsDrawer = lazy(() =>
  import('src/clinic/components/ClinicManageBookingsPage/components/BookedEventLogsDrawer')
)
const PaymentLinksViewer = lazy(() =>
  import('src/clinic/components/ClinicManageBookingsPage/slotItem/component/PaymentLinksViewer')
)
const EditBookingDialogComponent = lazy(() =>
  import('src/clinic/components/ClinicManageBookingsPage/ClinicManageBookingComponents').then(
    (module) => ({ default: module.EditBookingDialogComponent }),
  ),
)
const EditPatientDialogComponent = lazy(() =>
  import('src/clinic/components/ClinicManageBookingsPage/ClinicManageBookingComponents').then(
    (module) => ({ default: module.EditPatientDialogComponent }),
  ),
)
const ReceiptPrintingComponent = lazy(() =>
  import('src/clinic/components/ClinicManageBookingsPage/receiptPrinting/ReceiptPrintingComponent')
)

// Styled Tabs matching support portal theme
const SupportTabs = muiStyled(Tabs, {
  shouldForwardProp: (prop) => prop !== 'isMobile',
})(({ theme, isMobile }) => ({
  backgroundColor: 'rgba(16, 185, 129, 0.04)',
  borderRadius: '8px',
  padding: isMobile ? '2px' : '4px',
  minHeight: 'auto',
  width: isMobile ? '100%' : 'auto',
  overflowX: isMobile ? 'auto' : 'visible',
  marginBottom: theme.spacing(3),
  '& .MuiTabs-indicator': {
    backgroundColor: 'rgba(16, 185, 129, 0.95)',
    height: '3px',
    borderRadius: '3px',
  },
  '& .MuiTabs-flexContainer': {
    gap: isMobile ? '2px' : '4px',
    overflowX: isMobile ? 'auto' : 'visible',
    '&::-webkit-scrollbar': {
      display: 'none',
    },
    scrollbarWidth: 'none',
  },
}))

const SupportTab = muiStyled(Tab, {
  shouldForwardProp: (prop) => prop !== 'isMobile',
})(({ theme, isMobile }) => ({
  textTransform: 'none',
  minWidth: isMobile ? '120px' : 0,
  fontWeight: theme.typography.fontWeightRegular,
  padding: isMobile ? '8px 12px' : '10px 20px',
  color: 'rgba(71, 85, 105, 0.85)',
  borderRadius: '6px',
  transition: 'all 0.2s ease',
  fontSize: isMobile ? '12px' : '14px',
  fontFamily: "'Inter', 'Outfit', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  whiteSpace: 'nowrap',
  flexShrink: 0,
  '&:hover': {
    color: 'rgba(16, 185, 129, 0.95)',
    backgroundColor: 'rgba(16, 185, 129, 0.08)',
    opacity: 1,
  },
  '&.Mui-selected': {
    color: 'rgba(16, 185, 129, 0.95)',
    fontWeight: theme.typography.fontWeightMedium,
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
  },
  '&.Mui-focusVisible': {
    backgroundColor: 'rgba(16, 185, 129, 0.12)',
  },
}))

export default function SupportTherapistAppointmentsPage() {
  const { mentorSlug } = useParams()
  const navigate = useNavigate()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const [mentorProfileData, setMentorProfileData] = useState(null)
  const [loading, setLoading] = useState(true)
  // Sub-tabs for appointments: 0: All Booked, 1: In Clinic, 2: Video Call, 3: Not Confirmed
  const [selectedTab, setSelectedTab] = useState(0)
  // Drawer state
  const [bookedEventDetailsDrawerOpen, setBookedEventDetailsDrawerOpen] = useState(false)
  const [selectedBookedSlotForDrawer, setSelectedBookedSlotForDrawer] = useState(null)
  // Dialog states
  const [joiningDetailsDialogOpen, setJoiningDetailsDialogOpen] = useState(false)
  const [joiningLinksLoading, setJoiningLinksLoading] = useState(false)
  const [joiningLinksEnrichedBookedSlot, setJoiningLinksEnrichedBookedSlot] = useState(null)
  const [eventLogsDrawerOpen, setEventLogsDrawerOpen] = useState(false)
  const [paymentLinksDialogOpen, setPaymentLinksDialogOpen] = useState(false)
  const [editPatientDialogOpen, setEditPatientDialogOpen] = useState(false)
  const [editBookingDialogOpen, setEditBookingDialogOpen] = useState(false)
  const [openReceiptPrintingDialog, setOpenReceiptPrintingDialog] = useState(false)
  const [dataForReceiptPrinting, setDataForReceiptPrinting] = useState({ eventData: null, eventId: null })
  const [rescheduleDrawerBooking, setRescheduleDrawerBooking] = useState(null)
  const listingRef = useRef(null)
  const supportEventTypeOptions = getAllowedSupportBookedEventTypeOptions(getUserRoles() || [])

  const openReceiptDialog = useCallback(() => setOpenReceiptPrintingDialog(true), [])

  useEffect(() => {
    async function fetchMentorData() {
      try {
        // Get mentor data from sessionStorage (set by SupportMentorsListing)
        const storedMentorData = sessionStorage.getItem('supportMentorData')
        if (storedMentorData) {
          try {
            const parsed = JSON.parse(storedMentorData)
            if (parsed?.slug === mentorSlug) {
              setMentorProfileData(parsed)
              setLoading(false)
              return
            }
          } catch (e) {
            console.error('Error parsing stored mentor data:', e)
          }
        }

        // If not in sessionStorage or slug doesn't match, fetch from API
        const resp = await getSPDataWithoutParams(`/api/secure/cc/support/teacher/profile?slug=${mentorSlug}`)
        if (resp?.status === 200 && isCommonResponseSuccessfulV2(resp)) {
          const data = getDataMapFromCommonResponse(resp)
          setMentorProfileData(data)
          sessionStorage.setItem('supportMentorData', JSON.stringify(data))
        } else {
          // If mentor not found, redirect back
          navigate('/support-home/practitioner-management/therapists')
        }
      } catch (error) {
        console.error('Error fetching mentor data:', error)
        navigate('/support-home/practitioner-management/therapists')
      } finally {
        setLoading(false)
      }
    }

    fetchMentorData()
  }, [mentorSlug, navigate])

  // Build API params based on selected tab
  const buildApiParamsForTab = useCallback((tabIndex) => {
    const baseParams = {
      showBlockedSession: true, // Show blocked sessions by default
    }

    switch (tabIndex) {
      case 0: // All Booked
        return {
          ...baseParams,
          statusIds: [BOOKING_STATUSES.BOOKED],
        }
      case 1: // In Clinic (Booked)
        return {
          ...baseParams,
          statusIds: [BOOKING_STATUSES.BOOKED],
          meetingSrc: SESSION_PLATFORM_OFFLINE, // 'offline' for in-clinic
        }
      case 2: // Video Call (Booked)
        return {
          ...baseParams,
          statusIds: [BOOKING_STATUSES.BOOKED],
          meetingSrc: SESSION_PLATFORM_ONLINE, // 'aumhum' for video call
        }
      case 3: // Booking Not Confirmed
        return {
          ...baseParams,
          statusIds: [BOOKING_STATUSES.BOOKING_INITIATED],
          paymentStatusIds: [BOOKING_PAYMENT_STATUSES.PENDING],
        }
      default:
        return baseParams
    }
  }, [])

  // Wrapper function for fetching booked sessions with mentorSlug (fallback to mentorId)
  // Strategy: First try with mentorSlug parameter, if that fails (API doesn't support it or returns error),
  // fall back to mentorId parameter
  const fetchBookedSessions = useCallback(async (page, listingApiSignature, params) => {
    // Ensure we have at least mentorSlug or mentorId
    if (!mentorSlug && !mentorProfileData?.id) {
      console.error('fetchBookedSessions: Both mentorSlug and mentorId are unavailable')
      return { status: 400, data: { message: 'Mentor slug or ID is required' } }
    }

    // Add tab-specific params to the params object
    const tabParams = buildApiParamsForTab(selectedTab)
    const paramsWithTabFilters = {
      ...params,
      ...tabParams,
    }

    // First attempt: Try with mentorSlug (if API supports it)
    if (mentorSlug) {
      const paramsWithMentorSlug = {
        ...paramsWithTabFilters,
        mentorSlug: mentorSlug,
      }
      
      try {
        const responseWithSlug = await getBookedSessionsForMentorSupport(
          page,
          listingApiSignature,
          paramsWithMentorSlug,
        )

        // Check if the response is successful (status 200 and successful response)
        if (
          responseWithSlug?.status === 200 &&
          isCommonResponseSuccessfulV2(responseWithSlug)
        ) {
          console.log(`Successfully fetched booked sessions using mentorSlug=${mentorSlug} with tab=${selectedTab} params`, tabParams)
          return responseWithSlug
        }

        // If mentorSlug failed, log and fall back to mentorId
        console.warn(
          `API call with mentorSlug=${mentorSlug} returned unsuccessful response, falling back to mentorId`,
          {
            status: responseWithSlug?.status,
            data: responseWithSlug?.data,
          },
        )
      } catch (error) {
        // If API call throws an error (e.g., API doesn't support mentorSlug parameter), fall back
        console.warn(
          `API call with mentorSlug=${mentorSlug} threw an error, falling back to mentorId`,
          error,
        )
      }
    }

    // Fallback: Use mentorId if available
    if (mentorProfileData?.id) {
      const paramsWithMentorId = {
        ...paramsWithTabFilters,
        mentorId: mentorProfileData.id,
      }

      console.log(`Fetching booked sessions using mentorId=${mentorProfileData.id} as fallback with tab=${selectedTab} params`, tabParams)
      return await getBookedSessionsForMentorSupport(page, listingApiSignature, paramsWithMentorId)
    }

    // If neither worked, return error
    console.error('fetchBookedSessions: Unable to fetch - both mentorSlug and mentorId attempts failed')
    return {
      status: 400,
      data: { message: 'Unable to fetch booked sessions: mentorSlug and mentorId both unavailable' },
    }
  }, [mentorSlug, mentorProfileData?.id, selectedTab, buildApiParamsForTab])

  // Handler for opening details drawer
  const handleOpenDetails = useCallback((bookedEvent) => {
    setSelectedBookedSlotForDrawer(bookedEvent)
    setBookedEventDetailsDrawerOpen(true)
  }, [])

  // Handler for refreshing booked events (no-op for now, can be implemented if needed)
  const handleRefreshBookedEvents = useCallback(() => {
    listingRef.current?.resetData?.()
  }, [])

  // Fetch public joining links for a booked event
  const fetchPublicJoiningLinks = useCallback(async (bookedEventId) => {
    if (!bookedEventId) return null
    try {
      const resp = await getCPDataWithParams('/api/secure/cc/support/events/video-session-tokens/get', {
        'booked-event-ids': bookedEventId,
      })
      if (resp?.status === 200 && Array.isArray(resp?.data)) {
        return resp.data
      }
    } catch (error) {
      console.error('Error fetching public joining links:', error)
    }
    return null
  }, [])

  // Action handlers for drawer
  const handleOpenJoiningDetails = useCallback(async () => {
    if (!selectedBookedSlotForDrawer) return
    
    setJoiningDetailsDialogOpen(true)
    setJoiningLinksLoading(true)
    setJoiningLinksEnrichedBookedSlot(selectedBookedSlotForDrawer)
    
    // Fetch public joining links
    const publicLinks = await fetchPublicJoiningLinks(selectedBookedSlotForDrawer?.id)
    
    // Update booked slot with public joining links
    setJoiningLinksEnrichedBookedSlot({
      ...selectedBookedSlotForDrawer,
      publicJoiningLinks: publicLinks || [],
    })
    setJoiningLinksLoading(false)
  }, [selectedBookedSlotForDrawer, fetchPublicJoiningLinks])

  const handleOpenEventLogs = useCallback(() => {
    setEventLogsDrawerOpen(true)
  }, [])

  const handleOpenPaymentLinks = useCallback(() => {
    setPaymentLinksDialogOpen(true)
  }, [])

  const handleOpenPatientDetails = useCallback(() => {
    setEditPatientDialogOpen(true)
  }, [])

  const handleEditBooking = useCallback(() => {
    setEditBookingDialogOpen(true)
  }, [])

  if (loading) {
    return (
      <>
        <SupportHeader heading={'Appointments'} showBackButton />
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '400px',
          }}
        >
          <CircularProgress />
        </Box>
      </>
    )
  }

  if (!mentorProfileData) {
    return (
      <>
        <SupportHeader heading={'Appointments'} showBackButton />
        <Box sx={{ padding: 3, textAlign: 'center' }}>
          Therapist not found
        </Box>
      </>
    )
  }

  return (
    <>
      <SupportHeader 
        heading={`Appointments - ${mentorProfileData?.name || 'Therapist'}`} 
        showBackButton 
      />
      <Box
        sx={{
          maxWidth: '1400px',
          margin: '0 auto',
          width: '100%',
          padding: { xs: '16px', sm: '24px' },
          boxSizing: 'border-box',
          overflowX: 'hidden',
        }}
      >
        {/* Sub-tabs for appointments */}
        <Box sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}>
          <SupportTabs
            value={selectedTab}
            onChange={(e, newValue) => setSelectedTab(newValue)}
            isMobile={isMobile}
          >
            <SupportTab label="All Booked" isMobile={isMobile} />
            <SupportTab label="In Clinic (Booked)" isMobile={isMobile} />
            <SupportTab label="Video Call (Booked)" isMobile={isMobile} />
            <SupportTab label="Booking Not Confirmed" isMobile={isMobile} />
          </SupportTabs>
        </Box>
        <CoreBookingsListing
          ref={listingRef}
          key={`appointments-${selectedTab}`}
          fetchBookedSessions={fetchBookedSessions}
          listingApiSignature="/api/secure/cc/support/events/all/paginated"
          listOfPaymentLinksApiSignature="/api/secure/cc/support/events/user-payment-link/get"
          showMentorName={false}
          hidePortalVoiceCallAction
          cancelAppointmentApiSignature="/api/secure/cc/support/events/actions/cancel"
          skipCancelCheckInGuard
          logsApiEndpoint="/api/secure/cc/support/events/logs/get"
          mentorProfileData={mentorProfileData}
          // Note: meetingSrcFilter, filterByBookedStatus, and showOnlyUnconfirmed are now handled
          // via API params in fetchBookedSessions, but we keep these for backward compatibility
          // in case CoreBookingsListing still does client-side filtering as a fallback
          meetingSrcFilter={
            selectedTab === 1
              ? SESSION_PLATFORM_OFFLINE
              : selectedTab === 2
              ? SESSION_PLATFORM_ONLINE
              : undefined
          }
          filterByBookedStatus={
            selectedTab === 3 ? false : true // Tab 3 (Not Confirmed) excludes booked, others include only booked
          }
          showOnlyUnconfirmed={selectedTab === 3}
          hideSessionTypeFilter={true}
          eventTypeOptions={supportEventTypeOptions}
          onOpenDetails={handleOpenDetails}
          onRescheduleBooking={(bookedEvent) => setRescheduleDrawerBooking(bookedEvent)}
          rightSideCustomComponent={({ event, defaultActions }) => (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
              {defaultActions}
              <SupportContactActions
                patientId={event?.patientId}
                practitionerId={mentorProfileData?.id || event?.mentorId}
                bookedEventId={event?.id}
                clinicBrandId={event?.clinicBrandId}
                clinicId={event?.clinicId}
                targetRole="patient"
                eventType={event?.eventType}
                title=""
                compact
                uiSurface="support-therapist-appointments-strip"
                sx={{ minWidth: 240 }}
              />
            </Box>
          )}
        />
      </Box>

      <SupportBookedEventRescheduleDrawer
        open={Boolean(rescheduleDrawerBooking)}
        bookedEvent={rescheduleDrawerBooking}
        onClose={() => setRescheduleDrawerBooking(null)}
        onSuccess={() => {
          setRescheduleDrawerBooking(null)
          handleRefreshBookedEvents()
        }}
      />

      {/* Booked Event Details Drawer */}
      {bookedEventDetailsDrawerOpen && (
        <Suspense fallback={<Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}><CircularProgress size={24} /></Box>}>
          <BookedEventDetailsDrawer
            open={bookedEventDetailsDrawerOpen}
            onClose={() => {
              setBookedEventDetailsDrawerOpen(false)
              setSelectedBookedSlotForDrawer(null)
            }}
            bookedSlot={selectedBookedSlotForDrawer}
            forMentor={false}
            showViewReportsButton={false}
            showPrescriptionPdfDownloadButtons={false}
            forceShowJoiningDetails={false}
            hideRescheduleDialog={true}
            refreshBookedEvents={() => {}}
            clinicAssociations={[]}
            headerActions={
              <SupportContactActions
                patientId={selectedBookedSlotForDrawer?.patientId}
                practitionerId={mentorProfileData?.id || selectedBookedSlotForDrawer?.mentorId}
                bookedEventId={selectedBookedSlotForDrawer?.id}
                clinicBrandId={selectedBookedSlotForDrawer?.clinicBrandId}
                clinicId={selectedBookedSlotForDrawer?.clinicId}
                targetRole="patient"
                eventType={selectedBookedSlotForDrawer?.eventType}
                uiSurface="support-therapist-appointment-drawer"
                title="Patient Contact"
              />
            }
            // Action handlers
            onOpenJoiningDetails={handleOpenJoiningDetails}
            onOpenEventLogs={handleOpenEventLogs}
            onOpenReportView={() => {}}
            onOpenPrescriptionDownload={() => {}}
            onOpenUploadPrescription={() => {}}
            onSendReportToDoctor={() => {}}
            onBookScreening={() => {}}
            onSendForTherapy={() => {}}
            onSendForAssessment={() => {}}
            onOpenTreatmentHistory={() => {}}
            onBookTherapy={() => {}}
            onRequestReassign={() => {}}
            onToggleRecording={() => {}}
            onMarkCompleted={() => {}}
            onOpenPatientDetails={handleOpenPatientDetails}
            onAddVitals={() => {}}
            onEditBooking={handleEditBooking}
            onReschedule={() => {}}
            onOpenPaymentLinks={handleOpenPaymentLinks}
            onGoToEvent={() => {}}
            openReceiptDialog={openReceiptDialog}
            setEventDataForReceiptPrinting={setDataForReceiptPrinting}
          />
        </Suspense>
      )}

      {/* Receipt / Invoice Print Dialog */}
      <Dialog
        open={openReceiptPrintingDialog}
        PaperComponent={ReceiptPrintDialogPaper}
        onClose={() => {
          setOpenReceiptPrintingDialog(false)
          setDataForReceiptPrinting({ eventData: null, eventId: null })
        }}
        className="receipt-print-dialog"
      >
        <Suspense fallback={<Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}><CircularProgress /></Box>}>
          <ReceiptPrintingComponent
            bookedEvent={dataForReceiptPrinting.eventData}
            bookedEventId={dataForReceiptPrinting.eventId}
            patientSearchApiSignature="/api/secure/cc/support/patient/search"
            closeDialog={() => {
              setOpenReceiptPrintingDialog(false)
              setDataForReceiptPrinting({ eventData: null, eventId: null })
            }}
            fetchDataWithoutParams={getSPDataWithoutParams}
            currentClinic={dataForReceiptPrinting.eventData ? {
              clinicBrandName: dataForReceiptPrinting.eventData?.clinicBrandName || dataForReceiptPrinting.eventData?.clinicBrandData?.brandName,
              clinicBrandShortName: dataForReceiptPrinting.eventData?.clinicBrandData?.shortName,
              brandName: dataForReceiptPrinting.eventData?.clinicBrandData?.brandName,
              title: dataForReceiptPrinting.eventData?.clinicLocation?.title || dataForReceiptPrinting.eventData?.clinicLocation?.name || dataForReceiptPrinting.eventData?.clinicName || dataForReceiptPrinting.eventData?.clinicLocationName,
              clinicLocationName: dataForReceiptPrinting.eventData?.clinicLocation?.title || dataForReceiptPrinting.eventData?.clinicLocation?.name || dataForReceiptPrinting.eventData?.clinicName,
              location: dataForReceiptPrinting.eventData?.clinicLocation
                ? { address: dataForReceiptPrinting.eventData.clinicLocation.address }
                : (dataForReceiptPrinting.eventData?.address ? { address: dataForReceiptPrinting.eventData.address } : null),
              address: dataForReceiptPrinting.eventData?.clinicLocation?.address || dataForReceiptPrinting.eventData?.address,
              mobile: dataForReceiptPrinting.eventData?.clinicLocation?.mobile || dataForReceiptPrinting.eventData?.mobile,
            } : null}
          />
        </Suspense>
      </Dialog>

      {/* Joining Details Dialog */}
      {joiningLinksEnrichedBookedSlot && (
        <Suspense fallback={<Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}><CircularProgress size={24} /></Box>}>
          <Dialog
            open={joiningDetailsDialogOpen}
            maxWidth="lg"
            fullWidth
            onClose={() => {
              setJoiningDetailsDialogOpen(false)
              setJoiningLinksEnrichedBookedSlot(null)
            }}
          >
            {joiningLinksLoading ? (
              <Box sx={{ p: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
                <CircularProgress size={32} />
                <Box sx={{ color: 'text.secondary', fontSize: '14px' }}>Loading joining links...</Box>
              </Box>
            ) : (
              <JoiningDetails
                bookedEventInfo={joiningLinksEnrichedBookedSlot}
                closeDialog={() => {
                  setJoiningDetailsDialogOpen(false)
                  setJoiningLinksEnrichedBookedSlot(null)
                }}
                refreshBookedSlots={handleRefreshBookedEvents}
                sendSecureSessionLinkToPatientSignature="/api/secure/cc/support/events/secure-join-link/send"
                sendPublicSessionLinkToPatientSignature="/api/secure/cc/support/events/public-join-link/send"
                showSendingLinkButtonsToSupport={true}
              />
            )}
          </Dialog>
        </Suspense>
      )}

      {/* Event Logs Drawer */}
      {selectedBookedSlotForDrawer && (
        <Suspense fallback={<Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}><CircularProgress size={24} /></Box>}>
          <BookedEventLogsDrawer
            open={eventLogsDrawerOpen}
            onClose={() => setEventLogsDrawerOpen(false)}
            bookedSlot={selectedBookedSlotForDrawer}
            apiEndpoint="/api/secure/cc/support/events/logs/get"
          />
        </Suspense>
      )}

      {/* Payment Links Dialog */}
      {selectedBookedSlotForDrawer && (
        <Suspense fallback={<Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}><CircularProgress size={24} /></Box>}>
          <Dialog
            open={paymentLinksDialogOpen}
            maxWidth="lg"
            fullWidth
            onClose={() => setPaymentLinksDialogOpen(false)}
          >
            <PaymentLinksViewer
              bookedSlot={selectedBookedSlotForDrawer}
              closeDialog={() => setPaymentLinksDialogOpen(false)}
              refreshBookedSlots={handleRefreshBookedEvents}
            />
          </Dialog>
        </Suspense>
      )}

      {/* Edit Patient Dialog */}
      {selectedBookedSlotForDrawer && (
        <Suspense fallback={<Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}><CircularProgress size={24} /></Box>}>
          <EditPatientDialogComponent
            open={editPatientDialogOpen}
            handleCloseDialog={() => setEditPatientDialogOpen(false)}
            patientId={selectedBookedSlotForDrawer?.patientId}
            clinicId={selectedBookedSlotForDrawer?.clinicId}
            clinicBrandId={selectedBookedSlotForDrawer?.clinicBrandId}
            patientSearchApiSignature="/api/secure/cc/support/patient/search"
          />
        </Suspense>
      )}

      {/* Edit Booking Dialog */}
      {selectedBookedSlotForDrawer && (
        <Suspense fallback={<Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}><CircularProgress size={24} /></Box>}>
          <EditBookingDialogComponent
            open={editBookingDialogOpen}
            forMentor={false}
            bookedEvent={selectedBookedSlotForDrawer}
            closeFunction={() => setEditBookingDialogOpen(false)}
            callbackFunctionAfterSave={() => {
              setEditBookingDialogOpen(false)
              handleRefreshBookedEvents()
            }}
            cancelAppointmentApiSignature="/api/secure/cc/support/events/actions/cancel"
            editBookingApiSignature="/api/secure/cc/support/events/edit"
          />
        </Suspense>
      )}
    </>
  )
}
